// Sektör paketleri — Set kur / Sete ekle pencerelerinin ortak seçim durumu (DR1/DR2/DR3/DR8).
// Durum bileşen yereldir (store'a girmez); paket listesi + üyelik `kpi-packages-store` önbelleğinden.
// Açılış kararı katalog VE paket listesi gelince bir kez verilir (`opening` geri çağrısı); liste
// hatası `null` olur → çağıran eski davranışa (Katalog sekmesi) düşer.

import { useEffect, useMemo, useState } from "react";
import { normalizeSectorCode, type KpiMetric, type KpiPackage } from "@mahir/shared";
import {
  applyCatalogChange,
  applyPackageToggle,
  emptySelection,
  retainActivePackages,
  itemsByPackageFromMembership,
  selectionContext,
  sortSuggestedFirst,
  type PackageSelectionState,
  type PackageSourceTab,
  type SelectionContext,
} from "../lib/kpi-package-selection";
import { useKpiPackagesStore } from "../stores/kpi-packages-store";

export interface ActivePackages {
  packages: KpiPackage[];
  membership: Record<string, string[]>;
}

/** Etkin paketler + üyelik; liste yüklenemezse `null` (DR2: Katalog sekmesine düşüş). */
export async function loadActivePackages(force = false): Promise<ActivePackages | null> {
  await useKpiPackagesStore.getState().loadPackages(force);
  const s = useKpiPackagesStore.getState();
  if (s.packagesError) return null;
  return {
    packages: s.packages.filter((p) => p.status === "active"),
    membership: s.packageMembership,
  };
}

export interface UsePackageSelectionArgs {
  open: boolean;
  catalog: KpiMetric[] | null;
  inSet?: ReadonlySet<string> | undefined;
  /** müşteri `sector` serbest metni (önerilen paket) */
  clientSector?: string | null | undefined;
  opening: (
    ctx: SelectionContext,
    packages: KpiPackage[] | null,
  ) => { tab: PackageSourceTab; state: PackageSelectionState };
}

export function usePackageSelection(a: UsePackageSelectionArgs) {
  // undefined = yükleniyor, null = liste hatası
  const [loaded, setLoaded] = useState<ActivePackages | null | undefined>(undefined);
  // Açılış kararı verilene dek kaynak alanı iskelet gösterir (etkileşimli ağaç yok — erken tıklama
  // sessizce kaybolmasın); sekme "package" ile başlar.
  const [tab, setTab] = useState<PackageSourceTab>("package");
  const [state, setState] = useState<PackageSelectionState>(emptySelection);
  const [ready, setReady] = useState(false);
  // DR8 odak isteği: aynı paket için tekrarlanan red de satırı yeniden odaklar (seq artar)
  const [focusRequest, setFocusRequest] = useState<{ packageId: string; seq: number } | null>(null);

  useEffect(() => {
    if (!a.open) return;
    setReady(false);
    setLoaded(undefined);
    setState(emptySelection());
    setTab("package");
    setFocusRequest(null);
    let cancelled = false;
    void loadActivePackages().then((r) => {
      if (!cancelled) setLoaded(r);
    });
    return () => {
      cancelled = true;
    };
  }, [a.open]);

  const itemsByPackage = useMemo(
    () => itemsByPackageFromMembership(loaded?.membership ?? {}),
    [loaded],
  );
  const ctx = useMemo(
    () => selectionContext(a.catalog ?? [], itemsByPackage, a.inSet),
    [a.catalog, itemsByPackage, a.inSet],
  );

  useEffect(() => {
    if (!a.open || ready || a.catalog === null || loaded === undefined) return;
    const o = a.opening(ctx, loaded?.packages ?? null);
    setTab(o.tab);
    setState(o.state);
    setReady(true);
  }, [a.open, ready, a.catalog, loaded, ctx]);

  // Liste yeniden yüklenince (Yeniden denetle / sunucu reddi) artık etkin olmayan paketler düşer
  useEffect(() => {
    if (!ready || !loaded) return;
    const active = new Set(loaded.packages.map((p) => p.id));
    setState((s) => retainActivePackages(s, active, ctx));
  }, [loaded]);

  const sector = normalizeSectorCode(a.clientSector ?? null);
  const sorted = useMemo(
    () => sortSuggestedFirst(loaded?.packages ?? [], sector),
    [loaded, sector],
  );
  const packageById = useMemo(() => new Map(sorted.list.map((p) => [p.id, p])), [sorted]);
  const deprecatedChecked = state.checked.filter(
    (id) => (packageById.get(id)?.deprecatedCount ?? 0) > 0,
  );

  return {
    ready,
    ctx,
    tab,
    setTab,
    state,
    setState,
    /** null = yükleniyor */
    packages: loaded === undefined ? null : sorted.list,
    loadFailed: loaded === null,
    hasActivePackages: (loaded?.packages.length ?? 0) > 0,
    suggested: sorted.suggested,
    showNoSuggestion: sector !== null && sorted.list.length > 0 && sorted.suggested.size === 0,
    deprecatedChecked,
    focusRequest,
    toggle: (id: string, on: boolean): void => {
      setState((s) => applyPackageToggle(s, id, on, ctx));
    },
    catalogChange: (next: ReadonlySet<string>): void => {
      setState((s) => applyCatalogChange(s, next, ctx));
    },
    recheck: async (): Promise<void> => {
      const r = await loadActivePackages(true);
      setLoaded(r);
    },
    /** DR8 — sunucu deprecated reddi: Paket sekmesi + satıra odak, seçim korunur, liste tazelenir */
    showDeprecated: async (packageId: string | null): Promise<void> => {
      setTab("package");
      if (packageId) setFocusRequest((f) => ({ packageId, seq: (f?.seq ?? 0) + 1 }));
      setLoaded(await loadActivePackages(true));
    },
  };
}
